import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/*
  Shared by Resource Test and Package Test.

  Containers, resource groups and packages all keep their own copies of resources rather than a
  reference, and the copies turn up in more than one field (resources, additionalresources,
  extras...). So the whole object is walked, and any object whose resourceId is the chosen
  resource's and which carries a Concurrent Uses value counts as a copy. Some shapes use
  PascalCase (ResourceId / ConcurrentUses), so both spellings are accepted.

  Copies are matched on resourceId, never id - id is the version, and changes on every save.

  The selectable resources fields are not walked: they now hold resource ids only, so they hold
  no copies, and any whole objects left in them from before are not used.
*/

function isSkipped(key: string): boolean {
  return key.toLowerCase().startsWith('selectableresources');
}

export interface Copy {
  trail: string[];      //the holder's name, the named objects passed through, then the copy
  valuePath: string;    //path to the copy's Concurrent Uses within the holder's JSON, e.g. resources/2/concurrentUses
  stored: any;
  correct: boolean;
}

export interface CopyTarget {
  resourceId: string;
  concurrentUses: number;
}

//Something fetched in full that may hold copies: a container, resource group, package or event
export interface Holder {
  kind: string;
  name: string;
  disabled: boolean;
  data: any;
}

//One copy of one resource inside one holder - a row of the results
export interface CopyRow {
  resource: any;
  holder: Holder;
  copy: Copy;
}

/*
  Every resource in turn, against every holder. Only the inside of a holder is searched, never the
  holder itself, so a container is not counted as a copy of itself. Sorted by resource, then holder,
  so a resource's rows sit together.
*/
export function checkAll(resources: any[], holders: Holder[]): CopyRow[] {
  const rows: CopyRow[] = [];

  resources.forEach(resource => {
    holders.forEach(holder => {
      const copies: Copy[] = [];

      Object.keys(holder.data ?? {}).forEach(k => {
        const child = holder.data[k];
        if (child == null || typeof child !== 'object' || isSkipped(k))
          return;
        const trail = (k != 'resources' && Array.isArray(child)) ? [holder.name, k] : [holder.name];
        findCopies(child, k, trail, resource, copies);
      });

      copies.forEach(copy => rows.push({ resource, holder, copy }));
    });
  });

  return rows;
}

/*
  The trail shows the names of the objects passed through, plus the field names that say which
  part of the holder a copy is in (additionalresources, extras...) - 'resources' is left out as it
  is the usual one and only adds noise.
*/
export function findCopies(value: any, path: string, trail: string[], target: CopyTarget, out: Copy[]) {
  if (value == null || typeof value !== 'object')
    return;

  if (!Array.isArray(value)) {
    const id = value.resourceId ?? value.ResourceId;
    const usesKey = 'concurrentUses' in value ? 'concurrentUses' : ('ConcurrentUses' in value ? 'ConcurrentUses' : null);

    if (id != null && id == target.resourceId && usesKey != null) {
      const stored = value[usesKey];
      out.push({
        trail: trail,
        valuePath: (path === '' ? '' : path + '/') + usesKey,
        stored: stored,
        correct: stored != null && Number(stored) === Number(target.concurrentUses)
      });
    }
  }

  const keys = Array.isArray(value) ? value.map((_, i) => String(i)) : Object.keys(value);

  keys.forEach(k => {
    const child = value[k];
    if (child == null || typeof child !== 'object' || (!Array.isArray(value) && isSkipped(k)))
      return;

    findCopies(child, path === '' ? k : path + '/' + k, childTrail(value, k, child, trail), target, out);
  });
}

function childTrail(value: any, k: string, child: any, trail: string[]): string[] {
  let result = trail;

  if (!Array.isArray(value) && k != 'resources' && Array.isArray(child))
    result = [...result, k];

  const name = Array.isArray(child) ? null : (child.name ?? child.Name);
  if (name != null)
    result = [...result, name];

  return result;
}

/*
  Empty containers. A container with nothing in its resources breaks the booking process, and as
  holders keep their own copies, a container can be empty in a package or group even when the
  container itself is not - so every copy is checked, and the holder too when it is a container.
*/

//One empty container inside one holder - a row of the empty container results
export interface EmptyContainer {
  holder: Holder;
  trail: string[];      //as for Copy - the holder's name, the named objects passed through, then the container
  path: string;         //path to the container within the holder's JSON, '' when the holder is the container
}

export function findAllEmptyContainers(holders: Holder[]): EmptyContainer[] {
  const rows: EmptyContainer[] = [];

  holders.forEach(holder => {
    const found: { trail: string[], path: string }[] = [];
    findEmptyContainers(holder.data, '', [holder.name], found);
    found.forEach(f => rows.push({ holder, trail: f.trail, path: f.path }));
  });

  return rows;
}

function findEmptyContainers(value: any, path: string, trail: string[], out: { trail: string[], path: string }[]) {
  if (value == null || typeof value !== 'object')
    return;

  if (!Array.isArray(value) && (value.isresourcecontainer ?? value.IsResourceContainer) === true) {
    const held = value.resources ?? value.Resources;
    if (!Array.isArray(held) || held.length == 0)
      out.push({ trail, path });
  }

  const keys = Array.isArray(value) ? value.map((_, i) => String(i)) : Object.keys(value);

  keys.forEach(k => {
    const child = value[k];
    if (child == null || typeof child !== 'object' || (!Array.isArray(value) && isSkipped(k)))
      return;

    findEmptyContainers(child, path === '' ? k : path + '/' + k, childTrail(value, k, child, trail), out);
  });
}

//The note and opened paths that make a JSON tree show an empty container in red
export function highlightEmpty(row: EmptyContainer): { notes: Map<string, string>, good: Set<string>, expand: Set<string> } {
  const notes = new Map<string, string>();
  const expand = new Set<string>();

  notes.set(row.path, 'container with no resources');

  const parts = row.path === '' ? [] : row.path.split('/');
  for (let i = 1; i <= parts.length; i++)
    expand.add(parts.slice(0, i).join('/'));

  return { notes, good: new Set<string>(), expand };
}

//The notes and opened paths that make a JSON tree show and highlight every copy - wrong ones in red, correct in green
export function highlightCopies(copies: Copy[], target: CopyTarget): { notes: Map<string, string>, good: Set<string>, expand: Set<string> } {
  const notes = new Map<string, string>();
  const good = new Set<string>();
  const expand = new Set<string>();

  copies.forEach(c => {
    if (c.correct) {
      notes.set(c.valuePath, 'matches');
      good.add(c.valuePath);
    }
    else
      notes.set(c.valuePath, 'should be ' + target.concurrentUses);

    const parts = c.valuePath.split('/');
    for (let i = 1; i < parts.length; i++)
      expand.add(parts.slice(0, i).join('/'));
  });

  return { notes, good, expand };
}

/*
  Every row of a list endpoint, enabled and disabled. The rest of the app calls these lists with
  false for the normal view, and it is not certain whether true means "include disabled" or
  "disabled only" - so both are asked for and merged on the given key. Fails only if both fail.
*/
export function loadBothLists(http: HttpClient, urlWithoutFlag: string, key: string): Observable<any[] | null> {
  const one = (flag: boolean) => http.get<any>(urlWithoutFlag + flag).pipe(
    map(response => response?.succeeded ? (response.data ?? []) as any[] : null),
    catchError(() => of(null))
  );

  return forkJoin([one(false), one(true)]).pipe(
    map(([enabled, disabled]) => {
      if (enabled == null && disabled == null)
        return null;

      const merged = new Map<string, any>();
      [...(enabled ?? []), ...(disabled ?? [])].forEach(row => {
        if (row != null && !merged.has(row[key]))
          merged.set(row[key], row);
      });
      return [...merged.values()];
    })
  );
}
